import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { type AnnouncementRecipientDto } from '@/app/models/AnnouncementRecipientDto';
import { useLatestReceivedAnnouncements, usePendingAnnouncements } from '@/app/modules/announcements/core/_hooks';
import { isMandatory, publishedLabel } from '@/app/modules/announcements/core/_utils';

type AnnouncementTabPanelProps = {
  onAnnouncementOpen: (announcement: AnnouncementRecipientDto) => void;
  onViewAll: () => void;
};

/** Aba Comunicados do menu de notificações: os últimos recebidos, com o obrigatório pendente marcado. */
const AnnouncementTabPanel = ({ onAnnouncementOpen, onViewAll }: AnnouncementTabPanelProps) => {
  const { data: announcements = [], isPending, isError, error } = useLatestReceivedAnnouncements();
  const { data: pending = [] } = usePendingAnnouncements();
  const pendingAnnouncementCount = pending.length;
  const pendingIds = new Set(pending.map((item) => item.id));
  const isEmpty = !isError && !isPending && announcements.length === 0;
  const pendingCountLabel = pendingAnnouncementCount === 1 ? '1 pendente' : `${pendingAnnouncementCount} pendentes`;

  return (
    <div aria-labelledby="notifications-tab-announcements" id="notifications-panel-announcements" role="tabpanel">
      <div className="d-flex align-items-center justify-content-between px-6 py-4 border-bottom bg-light-secondary">
        <span className="text-muted fw-semibold fs-7">{pendingCountLabel}</span>
        <button className="btn btn-sm btn-secondary fw-bold" type="button" onClick={onViewAll}>
          Ver todos
        </button>
      </div>
      <div className="scroll-y mh-325px py-2">
        {isError ? <div className="alert alert-danger mx-6 my-4">{getErrorMessage(error)}</div> : null}
        {isEmpty ? (
          <div className="text-center py-10">
            <i className="bi bi-megaphone fs-3x text-gray-400 d-block mb-3" />
            <div className="fw-bold text-gray-700">Nenhum comunicado por aqui</div>
          </div>
        ) : null}
        <ul className="list-unstyled mb-0">
          {announcements.map((announcement) => (
            <li key={announcement.id}>
              <button
                className="btn btn-active-light w-100 text-start px-6 py-3 rounded-0"
                type="button"
                onClick={() => onAnnouncementOpen(announcement)}
              >
                <span className="d-flex align-items-center justify-content-between gap-2">
                  <span className="fw-semibold text-gray-800 fs-6">{announcement.title}</span>
                  {pendingIds.has(announcement.id) && isMandatory(announcement.type.value) ? (
                    <span className="badge badge-light-danger fs-8">Pendente</span>
                  ) : null}
                </span>
                <span className="d-block text-muted fs-7">
                  {publishedLabel(announcement.published_by_user_name, announcement.published_date)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default AnnouncementTabPanel;
